import { motion } from "motion/react";

import Link from "~/atoms/Link";
import useCheckAccountSetup from "~/hooks/useCheckAccountSetup";

type AccountSetupButtonProps = {
  position: "top" | "bottom";
};

const AccountSetupButton = ({ position }: AccountSetupButtonProps) => {
  const { data, isLoading } = useCheckAccountSetup();

  const setupData = {
    properties: data?.properties || false,
    tenants: data?.tenants || false,
    userVerifiedEmail: data?.userVerifiedEmail || false,
    businessInfo: data?.businessInfo || false,
    payments: data?.payments || false,
  };

  const numberStepsTotal = Object.keys(setupData || {}).length;

  const numberStepsComplete = Object.values(setupData || {}).filter(
    (step) => step === "complete"
  ).length;

  if (isLoading || numberStepsComplete === numberStepsTotal) {
    return null;
  }

  if (data.setupSkipped && position === "top") {
    return null; // Skip button if setup is already skipped
  }

  if (!data.setupSkipped && position === "bottom") {
    return null; // Skip button if no steps are complete
  }

  const stepKeys = Object.keys(setupData);

  return (
    <li className={`${position === "bottom" ? "mt-4" : ""}`}>
      <motion.div
        initial={{ opacity: 0, y: -20, height: 0 }}
        animate={{ opacity: 1, y: 0, height: "auto" }}
        exit={{ opacity: 0, y: -20, height: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <Link
          href="/landlord/account-setup"
          className="mb-3 block w-full max-w-md rounded-lg border border-blue-200 bg-blue-100 p-3 shadow-sm transition"
        >
          <span className="mb-1 flex items-center justify-between">
            <span className="text-sm font-bold text-gray-900">
              Finish setting up your account
            </span>
          </span>

          <span className="mt-1 mb-2 block text-xs font-semibold text-blue-600">
            {numberStepsComplete}/{numberStepsTotal} complete{" "}
            <span className="ml-1">🚀</span>
          </span>

          <span className="flex gap-1">
            {stepKeys.map((stepKey, index) => (
              <span
                key={stepKey}
                className={`h-2 flex-1 rounded-full ${
                  index < numberStepsComplete ? "bg-blue-700" : "bg-blue-400"
                }`}
              />
            ))}
          </span>
        </Link>
      </motion.div>
    </li>
  );
};

export default AccountSetupButton;
